import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useDrawer from 'common/hooks/useDrawer';
import usePublishData from './hooks/usePublishData';
import WorklogDetails from 'components/WorklogDetails/WorklogDetails';
import { PAGE_CONTEXTS } from 'components/WorklogDetails/constants';
import { ScheduleDrawer } from './modals/ScheduleDrawer';
import ScheduleModals from './modals/ScheduleModals';
import ShiftSelectionModal from './modals/ShiftSelectionModal';
import AdminMobileSchedulePage from './views/mobile/AdminMobileSchedulePage';
import ScheduleV6PageLayout from './ScheduleV6PageLayout';
import { updateSheets } from 'store/schedule/schedulePageOperation';
import modalsPageActions from 'store/modals/modalsPageActions';
import { PUBLISH_SHIFT } from 'pages/ScheduleV6/constants/shifts';
import useScheduleResourcesLoad from './resources/hooks/useScheduleResourcesLoad';

const defaultEmptyArray = [];

/**
 * Shared infrastructure shell for ScheduleV6.
 *
 * Responsibilities:
 * - manage drawer state (main + additional) shared by both views
 * - own usePublishData and expose independent publish/update modal openers
 * - render WorklogDetails, ScheduleDrawer, ScheduleModals (once, for both views)
 * - render the appropriate view (mobile or desktop) with wired props
 */
const SchedulePageShell = ({
  isMobile,
  selectedDate,
  scheduleDayView,
  scheduleView,
  showAllLocations,
  filters,
  dateFormat,
  timeFormat,
  openSnackbar,
  openModal,
}) => {
  const dispatch = useDispatch();
  useScheduleResourcesLoad();

  const { drawer, openDrawer, closeDrawer } = useDrawer();
  const {
    drawer: additionalDrawer,
    openDrawer: openAdditionalDrawer,
    closeDrawer: closeAdditionalDrawer,
  } = useDrawer();

  const [noClSheets, setNoClSheets] = useState(defaultEmptyArray);
  const [publishShift, setPublishShift] = useState(PUBLISH_SHIFT.FULL);
  const [updateShift, setUpdateShift] = useState(PUBLISH_SHIFT.FULL);
  const [shiftPickerOpen, setShiftPickerOpen] = useState(false);
  const pendingPublish = useRef(false);
  const pendingUpdate = useRef(false);

  const updateModalOpen = useSelector((store) => store.modals.modals.update?.open);

  const { publish, update } = usePublishData(publishShift, updateShift);

  const canPublish = publish.availableShifts.size > 0;
  const canUpdate = update.availableShifts.size > 0;

  const openModalFor = useCallback((type, dataset, selectedShift, onShiftChange) => {
    if (dataset.error?.text) {
      setNoClSheets(dataset.error.type === 'no-cl' ? dataset.noClSheets : defaultEmptyArray);
      openModal('info', dataset.error.text);
      return;
    }
    setNoClSheets(defaultEmptyArray);
    openModal(type, {
      type,
      workers: dataset.workers,
      ids: dataset.ids,
      availableShifts: dataset.availableShifts,
      selectedShift,
      onShiftChange,
    });
  }, [openModal]);

  // Keep the open Update modal in sync when its inline shift selector changes.
  useEffect(() => {
    if (!updateModalOpen) return;
    dispatch(modalsPageActions.setModalData({
      name: 'update',
      data: {
        type: 'update',
        workers: update.workers,
        ids: update.ids,
        availableShifts: update.availableShifts,
        selectedShift: updateShift,
        onShiftChange: setUpdateShift,
      },
    }));
  }, [updateModalOpen, updateShift, update.workers, update.ids, update.availableShifts, dispatch]);

  // Open each modal once its shift has settled (after a picker choice or a reset).
  useEffect(() => {
    if (!pendingPublish.current) return;
    pendingPublish.current = false;
    openModalFor('publish', publish, publishShift, setPublishShift);
  }, [publishShift, publish, openModalFor]);

  useEffect(() => {
    if (!pendingUpdate.current) return;
    pendingUpdate.current = false;
    openModalFor('update', update, updateShift, setUpdateShift);
  }, [updateShift, update, openModalFor]);

  const showPublishModal = () => {
    setNoClSheets(defaultEmptyArray);
    if (publish.availableShifts.size > 1) {
      setShiftPickerOpen(true);
      return;
    }
    if (publishShift !== PUBLISH_SHIFT.FULL) {
      pendingPublish.current = true;
      setPublishShift(PUBLISH_SHIFT.FULL);
      return;
    }
    openModalFor('publish', publish, publishShift, setPublishShift);
  };

  const showUpdateModal = () => {
    setNoClSheets(defaultEmptyArray);
    if (updateShift !== PUBLISH_SHIFT.FULL) {
      pendingUpdate.current = true;
      setUpdateShift(PUBLISH_SHIFT.FULL);
      return;
    }
    openModalFor('update', update, updateShift, setUpdateShift);
  };

  const handleShiftConfirm = (shift) => {
    setShiftPickerOpen(false);
    if (shift === publishShift) {
      openModalFor('publish', publish, publishShift, setPublishShift);
      return;
    }
    pendingPublish.current = true;
    setPublishShift(shift);
  };

  // Wraps openAdditionalDrawer for WorklogDetails which passes (type, subType, material)
  const openWlAdditionalDrawer = (type, subType, material) => () => {
    const data = {};
    if (subType) data.subType = subType;
    if (material) data.material = material;
    openAdditionalDrawer(type, data);
  };

  // Curried wrapper matching the (type) => () => open(type) API expected by mobile header/components
  const curriedOpenDrawer = useCallback(
    (type) => () => openDrawer(type),
    [openDrawer]
  );

  // Used by WorklogDetails on mobile to refresh the sheet list after edits
  const mobileViewSheetsRefresh = useCallback(() => dispatch(updateSheets()), [dispatch]);

  return (
    <>
      {isMobile ? (
        <AdminMobileSchedulePage
          openDrawer={curriedOpenDrawer}
          openSnackbar={openSnackbar}
          showPublishModal={showPublishModal}
          showUpdateModal={showUpdateModal}
          canPublish={canPublish}
          canUpdate={canUpdate}
          showReconcileModal={() => openModal('reconcile', 'Do you wish to Reconcile Schedule?')}
        />
      ) : (
        <ScheduleV6PageLayout
          selectedDate={selectedDate}
          scheduleDayView={scheduleDayView}
          scheduleView={scheduleView}
          showAllLocations={showAllLocations}
          filters={filters}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          openDrawer={openDrawer}
          closeDrawer={closeDrawer}
          showPublishModal={showPublishModal}
          showUpdateModal={showUpdateModal}
          canPublish={canPublish}
          canUpdate={canUpdate}
          showReconcileModal={() => openModal('reconcile', 'Do you wish to Reconcile Schedule?')}
          noClSheets={noClSheets}
        />
      )}

      <ShiftSelectionModal
        isOpen={shiftPickerOpen}
        onClose={() => setShiftPickerOpen(false)}
        onConfirm={handleShiftConfirm}
        availableShifts={publish.availableShifts}
      />

      <WorklogDetails
        openSnackbar={openSnackbar}
        updateVisibleSheets={() => {}}
        openDrawer={openDrawer}
        openAdditionalDrawer={openWlAdditionalDrawer}
        updateDrawerFromRouter={closeDrawer}
        sheetIds={[]}
        pageCount={0}
        hasMore={false}
        pageContext={PAGE_CONTEXTS.schedule}
        mobileViewSheetsRefresh={isMobile ? mobileViewSheetsRefresh : undefined}
      />
      <ScheduleDrawer
        drawer={drawer}
        additionalDrawer={additionalDrawer}
        openSnackbar={openSnackbar}
        closeDrawer={closeDrawer}
        closeAdditionalDrawer={closeAdditionalDrawer}
        selectedDate={selectedDate}
        isMobile={isMobile}
      />
      <ScheduleModals openSnackbar={openSnackbar} openModal={openModal} />
    </>
  );
};

SchedulePageShell.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  scheduleDayView: PropTypes.string.isRequired,
  scheduleView: PropTypes.string.isRequired,
  showAllLocations: PropTypes.bool,
  filters: PropTypes.object,
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  openSnackbar: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
};

export default SchedulePageShell;
